// Setup → Users (admin): roles, skills (Phase 2) and the RingCentral extension ID per user (GET/PATCH /api/setup/users).
// The extension ID lets the server match RingCentral telephony sessions to the operator who dialed,
// even when the widget's report of the call is missed. Each row saves on its own; changes are audited.

import { useState } from 'react';
import { ALL_ROLES, api, type Role, type SetupUser, type SetupUserPatch, type Skill } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { Card, Empty, ErrorBox, InlineError, Loading, StatusPill } from '../../components/ui.tsx';
import { useLoad } from '../../hooks.ts';
import { useSession } from '../../session.tsx';
import { fmtLocal } from '../../time.ts';
import { SkillPicker } from './common.tsx';
import { EXTENSION_NUMBER_HINT, looksLikeExtensionNumber } from './extension-id.ts';

interface Draft {
  roles: Role[];
  rc: string;
  skills: string[];
}

const draftOf = (u: SetupUser): Draft => ({ roles: ALL_ROLES.filter((r) => u.roles.includes(r)), rc: u.rcExtensionId ?? '', skills: (u.skills ?? []).slice() });

function patchFor(u: SetupUser, d: Draft): SetupUserPatch {
  const out: SetupUserPatch = {};
  const before = ALL_ROLES.filter((r) => u.roles.includes(r));
  if (before.join() !== d.roles.join()) out.roles = d.roles;
  const rc = d.rc.trim() || null;
  if (rc !== (u.rcExtensionId ?? null)) out.rcExtensionId = rc;
  if ([...d.skills].sort().join() !== [...(u.skills ?? [])].sort().join()) out.skills = d.skills;
  return out;
}

function UserRow({ u, meId, skills, onSaved }: { u: SetupUser; meId: string; skills: Skill[] | undefined; onSaved: (u: SetupUser) => void }) {
  const [d, setD] = useState<Draft>(() => draftOf(u));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const patch = patchFor(u, d);
  const dirty = Object.keys(patch).length > 0;
  const rcBad = d.rc.trim() !== '' && !/^\d{1,20}$/.test(d.rc.trim());
  const rcShort = !rcBad && looksLikeExtensionNumber(d.rc);
  // The server refuses any roles change on yourself that leaves out admin.
  const selfDemote = u.id === meId && patch.roles !== undefined && !d.roles.includes('admin');

  const toggle = (r: Role, on: boolean) => {
    setSaved(false);
    setD((x) => ({ ...x, roles: ALL_ROLES.filter((k) => (k === r ? on : x.roles.includes(k))) }));
  };
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const nu = await api.patchSetupUser(u.id, patch);
      onSaved(nu);
      setD(draftOf(nu));
      setSaved(true);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className={u.status === 'active' ? '' : 'inactive'}>
      <td>
        <span className="strong">{u.displayName}</span>
        <div className="note mono">{u.upn}</div>
      </td>
      <td>
        <StatusPill status={u.status} />
      </td>
      {ALL_ROLES.map((r) => (
        <td key={r} className="center">
          <input type="checkbox" checked={d.roles.includes(r)} onChange={(e) => toggle(r, e.target.checked)} aria-label={`${u.displayName}: ${r}`} disabled={busy} />
        </td>
      ))}
      <td>
        <input
          className="compact mono rcext"
          value={d.rc}
          onChange={(e) => {
            setSaved(false);
            setD((x) => ({ ...x, rc: e.target.value }));
          }}
          placeholder="e.g. 62845001"
          inputMode="numeric"
          aria-label={`${u.displayName}: RingCentral extension ID`}
          aria-invalid={rcBad}
          disabled={busy}
        />
      </td>
      <td className="skillcell">
        {skills ? (
          <SkillPicker skills={skills} value={d.skills} onChange={(v) => (setSaved(false), setD((x) => ({ ...x, skills: v })))} disabled={busy} label={`${u.displayName}: skills`} />
        ) : (
          <span className="note">{(u.skills ?? []).join(', ') || '—'}</span>
        )}
        {u.groups && u.groups.length > 0 && (
          <div className="note">
            Groups: {u.groups.map((g) => (g.primary ? `${g.name} (primary)` : g.name)).join(', ')}
          </div>
        )}
      </td>
      <td className="mono nowrap small">{fmtLocal(u.lastLoginAt)}</td>
      <td className="nowrap">
        <button type="button" className="btn sm pri" onClick={() => void save()} disabled={!dirty || busy || rcBad || selfDemote}>
          <Icon name="check" />
          {busy ? 'Saving…' : 'Save'}
        </button>{' '}
        {dirty && !busy && (
          <button type="button" className="btn sm" onClick={() => (setD(draftOf(u)), setErr(null))}>
            Undo
          </button>
        )}
        {saved && !dirty && <span className="ok-t small"> Saved</span>}
        {rcBad && <div className="note warn-t">The extension ID is the numeric RingCentral ID, not the short extension number.</div>}
        {rcShort && <div className="note warn-t">{EXTENSION_NUMBER_HINT}</div>}
        {selfDemote && <div className="note warn-t">You cannot remove your own admin role.</div>}
        <InlineError error={err} />
      </td>
    </tr>
  );
}

export function UsersEditor() {
  const s = useSession();
  const load = useLoad((signal) => api.setupUsers(signal), []);
  const skills = useLoad((signal) => api.skills(signal), []);
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const rows = (load.data ?? [])
    .filter((u) => !needle || `${u.displayName} ${u.upn} ${u.rcExtensionId ?? ''}`.toLowerCase().includes(needle))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
  return (
    <Card
      title={
        <>
          <Icon name="users" />
          Users
        </>
      }
      right={<input type="search" className="compact" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users" aria-label="Search users" />}
    >
      <div className="note">
        Roles decide what each person can do. Skills decide which sites' events they are offered (a site can require skills); group membership is set in Groups and routing. The RingCentral extension ID ties an operator's calls to them when RingCentral reports a call the console did not; find it in the
        RingCentral admin portal (the numeric ID, not the extension number). Changes are audited.
      </div>
      {load.error && !load.data ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load users" />
      ) : !load.data ? (
        <Loading label="Loading users…" />
      ) : rows.length === 0 ? (
        <Empty>{needle ? 'No users match.' : 'No users yet. People appear here after their first sign-in.'}</Empty>
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">User</th>
                <th scope="col">Status</th>
                {ALL_ROLES.map((r) => (
                  <th key={r} scope="col" className="center">
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </th>
                ))}
                <th scope="col">RingCentral extension ID</th>
                <th scope="col">Skills</th>
                <th scope="col">Last sign-in</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <UserRow key={u.id} u={u} meId={s.me.userId} skills={skills.data} onSaved={(nu) => load.setData((l) => (l ?? []).map((x) => (x.id === nu.id ? nu : x)))} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
